import React, { useState, useEffect } from 'react';
import {
  Chip,
  Slider,
  Form,
  Button,
  Input,
  Textarea,
  Spinner,
  Tooltip,
  Badge,
  Checkbox,
  Accordion,
  AccordionItem,
} from "@heroui/react";
import CampaignPreview from './CampaignPreview';
import ZeroBounceSDK from '@zerobounce/zero-bounce-sdk';

const CampaignForm = ({cancelForm, user, campaign, stompClient, setBanner}) => {
  const [mailerSubject, setMailerSubject] = useState(campaign?.subject);
  const [recipients, setRecipients] = useState(campaign?.recipients || []);
  const [toRecipients, setToRecipients] = useState(campaign?.to_recipients || []);
  const [ccRecipients, setCcRecipients] = useState(campaign?.cc_recipients || []);
  const [recipient, setRecipient] = useState();
  const [toRecipient, setToRecipient] = useState();
  const [ccRecipient, setCcRecipient] = useState();
  const [emailValidations, setEmailValidations] = useState();
  const [mailerContent, setMailerContent] = useState(campaign?.mailer_content);
  const [vanityUrl, setVanityUrl] = useState(campaign?.vanity_url);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState();
  const [uriErrors, setUriErrors] = useState();
  const [bodyError, setBodyError] = useState();
  const [subjectError, setSubjectError] = useState();
  const [showFooter, setShowFooter] = useState(campaign ? campaign.include_footer : true);
  const [typos, setTypos] = useState(campaign ? campaign.typos : true);
  const [variability, setVariability] = useState(campaign ? (campaign?.variability * 10) : 5);
  const [tone, setTone] = useState(campaign ? campaign.tone : 5);
  const [fetchingCompliance, setFetchingCompliance] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [promptInject, setPromptInject] = useState(campaign?.prompt_inject);
  const [isInitialTooltipOpen, setIsInitialTooltipOpen] = useState(false);
  const [isVanityTooltipOpen, setIsVanityTooltipOpen] = useState(false);
  const [isToEmailTooltipOpen, setIsToEmailTooltipOpen] = useState(false);
  const [isCcEmailTooltipOpen, setIsCcEmailTooltipOpen] = useState(false);
  const [isBccEmailTooltipOpen, setIsBccEmailTooltipOpen] = useState(false);
  const [isBodyTooltipOpen, setIsBodyTooltipOpen] = useState(false);
  const [isVariabilityTooltipOpen, setIsVariabilityTooltipOpen] = useState(false);
  const [isToneTooltipOpen, setIsToneTooltipOpen] = useState(false);
  const zsdk = new ZeroBounceSDK();
  zsdk.init('e2a89686b39d4d4ab82d99a69db516fd');

  useEffect(() => {
    if (campaign) {
      const validateEmails = async () => {
        const emailAddresses = [].concat(toRecipients).concat(recipients).concat(ccRecipients);
        if (emailAddresses && emailAddresses.length > 0) {
          const validations = await zsdk.validateBatch(emailAddresses.map(rec => {return {email_address: rec}}));
          let newValidations = (emailValidations && emailValidations.length > 0) ? [...emailValidations] : [];
          setEmailValidations(newValidations.concat(validations['email_batch']));
        }
      };
      validateEmails();
    }
    // eslint-disable-next-line
  }, [campaign]);

  const submitCampaign = async (e) => {
    setLoading(true);
    setUriErrors();
    setValidationErrors();
    e.preventDefault();

    if (!vanityUrl) {
      setUriErrors('Must name your cause!');
      setLoading(false);
      return false;
    }
    if ((campaign && vanityUrl !== campaign.vanity_url) || !campaign) {
      const vanityCheck = await validateUrl(vanityUrl);
      if (!vanityCheck) {
        setLoading(false);
        return false;
      }
    }
    let errors = [];
    if (!mailerSubject) {
      setSubjectError('Must specify a subject for your email.');
    }
    if ((!recipients || recipients?.length === 0) && (!toRecipients || toRecipients?.length === 0) && (!ccRecipients || ccRecipients?.length === 0)) {
      errors.push("No recipients specified!");
    }
    if (!mailerContent) {
      setBodyError("Body cannot be empty.");
    }
    if (errors.length > 0 || !mailerContent) {
      setValidationErrors(errors);
      setLoading(false);
      return null;
    }

    const formData = new FormData();
    formData.append('email', user.email);
    formData.append('vanity_url', vanityUrl);
    formData.append('subject', mailerSubject);
    formData.append('recipients', recipients);
    formData.append('to_recipients', toRecipients);
    formData.append('cc_recipients', ccRecipients);
    formData.append('include_footer', showFooter);
    formData.append('mailer_content', mailerContent);
    let language = undefined;
    if (navigator.languages !== undefined) {
      language = navigator.languages[0];
    } else {
      language = navigator.language;
    }
    formData.append('language', language);
    formData.append('variability', variability / 10);
    if (promptInject && promptInject !== 'undefined' && promptInject !== '') {
      formData.append('prompt_inject', promptInject);
    }
    if (user.active_organization_id) {
      formData.append('organization_id', user.active_organization_id);
    }
    formData.append('typos', typos);
    formData.append('tone', tone);
    const timeout = 180000;
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    const result = await fetch(`/api/v1/campaign${campaign?._id ? ('/' + campaign._id) : ''}`, {
      method: campaign?._id ? 'PUT' : 'POST',
      body: formData,
      signal: controller.signal
    });
    clearTimeout(id);
    const data = await result.json();
    if (data && result.status === 200) {
      // Success... Hide form and show status.
      cancelForm();
      setBanner(`Campaign ${campaign?._id ? 'updated' : 'created'} successfully! ${campaign?._id ? 'Updates' : 'Campaign generation'} may take a few moments`);
      return true;
    } else {
      // Failure..
      return false;
    }
  };

  const verifyAllEmails = () => {
    if (emailValidations && emailValidations.length > 0) {
      const echeck = emailValidations?.filter(e => e['status'] === 'invalid');
      if (echeck && echeck.length > 0) {
        return false;
      }
    }
    return true;
  };

  const headerByteCounter = () => {
    return (toRecipients.join(',').length + ccRecipients.join(',').length + recipients.join(',').length + mailerSubject.length);
  };

  const showEmailStatus = (email) => {
    if (emailValidations && emailValidations.length > 0) {
      const addr = emailValidations.filter(e => e['address'] === email);
      if (addr && addr.length > 0) {
        const thisAddr = addr[0];
        if (thisAddr.status !== 'valid') {
          let alertContent = 'Address status: ' + addr[0]['status'];
          if (addr[0]['sub_status']) {
            alertContent += ' - ' + addr[0]['sub_status'];
          }
          alert(alertContent);
        }
      }
    }
  };

  const checkCompliance = async () => {
    setFetchingCompliance(true);
    const fd = new FormData();
    fd.append('campaign_text', mailerContent);
    const check = await fetch(`/api/v1/campaigns/compliance`, {method: 'POST'});
    setFetchingCompliance(false);
    if (check.status === 200) {
      const output = await check.json();
      alert(output['compliance']['compliance']);
    } else {
      alert('Unable to determine compliance.');
    }
  };

  const validateUrl = async () => {
    if (vanityUrl.indexOf(' ') !== -1) {
      setUriErrors('The name of the campaign cant have spaces or illegal characters - instead of space use _');
      return false;
    }
    const regex = /^[a-zA-Z0-9-_]+$/;
    if (!regex.test(vanityUrl)) {
      setUriErrors('The name of the campaign cant have illegal characters - use only numbers, letters, dashes, or underscores');
      return false;
    }
    const check = await fetch(`/api/v1/uri_check?uri=${vanityUrl}`);
    if (check.status === 200) {
      setUriErrors();
      return true;
    } else {
      setUriErrors(`"${vanityUrl}" is already taken. Please choose another campaign name.`);
      return false;
    }
  };

  const handleEmailInputString = (data) => {
    let emailList = [];
    if (data.indexOf(',') !== -1) {
      emailList = data.split(',').filter(e => e.indexOf('@') !== -1).map(t => t.trim().toLowerCase());
    } else {
      emailList = data.split(' ').filter(e => e.indexOf('@') !== -1).map(t => t.trim().toLowerCase());
    }
    return emailList;
  };

  const removeRecipient = (r) => {
    let newEmailValidations = (emailValidations && emailValidations.length > 0) ? [...emailValidations] : [];
    setEmailValidations(newEmailValidations.filter(e => e.address !== r));
    setRecipients(recipients.filter(f => f !== r));
  }

  const removeToRecipient = (r) => {
    let newEmailValidations = (emailValidations && emailValidations.length > 0) ? [...emailValidations] : [];
    setEmailValidations(newEmailValidations.filter(e => e.address !== r));
    setToRecipients(toRecipients.filter(f => f !== r));
  }

  const removeCcRecipient = (r) => {
    let newEmailValidations = (emailValidations && emailValidations.length > 0) ? [...emailValidations] : [];
    setEmailValidations(newEmailValidations.filter(e => e.address !== r));
    setCcRecipients(ccRecipients.filter(f => f !== r));
  }

  const toggleFooter = () => {
    if (showFooter) {
      setShowFooter(false);
    } else {
      setShowFooter(true);
    }
  }

  const toggleTypos = () => {
    if (typos) {
      setTypos(false);
    } else {
      setTypos(true);
    }
  }

  const getEmailValidationColor = (email) => {
    if (emailValidations && emailValidations.length > 0) {
      const thisValidation = emailValidations.filter(e => e.address === email);
      if (thisValidation && thisValidation.length > 0) {
        if (thisValidation[0].status === 'valid') {
          return 'success';
        } else if (thisValidation[0].status === 'catch-all' || thisValidation[0].status === 'do_not_mail') {
          return 'warning';
        } else {
          return 'danger';
        }
      }
    }
    return 'default';
  };

  const setRecipientsValidated = async (recArray, setter) => {
    setValidationErrors([]);
    setter(recArray);
    const recs = recArray.map(rec => {return {email_address: rec}});
    const results = await zsdk.validateBatch(recs);
    let newValidations = emailValidations ? [...emailValidations] : [];
    setEmailValidations(newValidations.concat(results['email_batch']));
  }

  const unpaidCampaign = () => {
    if (user?.super_unlimited) {
      return false;
    }
    if (campaign && campaign.limit > 100) {
      return false;
    }
    return true;
  };

  return (
    <div className='campaign-form'>
      { showPreview && <CampaignPreview user={user} cancelPreview={() => setShowPreview(false)}
                                        language={(navigator && navigator.language) ? navigator.language : navigator.languages[0]}
                                        setBanner={setBanner}
                                        cancelForm={cancelForm}
                                        mailer_content={mailerContent} subject={mailerSubject} tone={tone}
                                        variability={variability} vanity_url={vanityUrl}
                                        stompClient={stompClient} typos={typos} footer={showFooter}
                                        campaign={campaign} prompt_inject={promptInject}
                                        toRecipients={toRecipients} ccRecipients={ccRecipients} bccRecipients={recipients} />
      }
      <Form validationErrors={validationErrors}
            onSubmit={submitCampaign}
            validationBehavior='aria'>
        <div className='vanity_url'>
          <span style={{marginTop: '5px', fontSize: '15px', fontWeight: 'bold', display: 'flex'}}>https://app.ActivistMailer.com/</span>
          <input className='vanity_url-input'
                 placeholder="my_cause"
                 value={vanityUrl}
                 minLength='4'
                 type="text"
                 variant="bordered"
                 onKeyDown={(d) => {if (d.keyCode === 13) {d.keyCode = 0; d.preventDefault() }}}
                 onChange={(d) => setVanityUrl(d.target.value)}
          />
          <div style={{display: 'flex'}}>
            <Tooltip isOpen={isVanityTooltipOpen} content="Create the name of the link for your campaign">
              <Badge onClick={() => {
                         if (isVanityTooltipOpen) {
                           setIsVanityTooltipOpen(false);
                         } else {
                           setIsVanityTooltipOpen(true);
                         }
                     }}
                     variant='solid' color="primary" content="?" size="sm" />
            </Tooltip>
          </div>
        </div>
        <div className='url-errors'>{uriErrors}</div>
        { loading
          ? <div>
              <Spinner />
              <div style={{width: '480px', display: 'inline-block'}}>
                <span style={{paddingLeft: '6px', fontSize: '14px'}}>
                  Processing. This may take a few minutes.
                </span>
              </div>
            </div>
          :
            ''
        }
        <p />
        <div className='helper'>
          <div className='campaign-form-input'>
            <Input style={{color: '#f0f0f0'}} isRequired label='Initial Subject of Email'
                   onKeyDown={(d) => {if (d.keyCode === 13) {d.keyCode = 0; d.preventDefault() }}}
                   placeholder="Attention please"
                   validationBehavior='aria'
                   fullWidth='true'
                   isInvalid={subjectError ? true : false}
                   errorMessage={subjectError}
                   type="text"
                   value={mailerSubject}
                   color='secondary'
                   variant="bordered"
                   onChange={(d) => {d.preventDefault(); setSubjectError(); setMailerSubject(d.target.value)}}
            />
            <div style={{display: 'flex'}}>
              <Tooltip isOpen={isInitialTooltipOpen} content="The initial subject line of the email">
                <Badge onClick={() => {
                         if (isInitialTooltipOpen) {
                           setIsInitialTooltipOpen(false);
                         } else {
                           setIsInitialTooltipOpen(true);
                         }
                       }}
                       variant='solid' color="primary" content="?" size="sm" />
              </Tooltip>
            </div>
          </div>
        </div>
        <div className='helper'>
          <div className='error-header'>
            {validationErrors ? validationErrors.map((x) => <div style={{paddingBottom:'8px'}}>{x}</div>) : ''}
          </div>

          { (toRecipients?.length === 0 && recipients?.length ===  0 && ccRecipients?.length === 0) &&
              <div style={{width: '100%', textAlign: 'left', paddingLeft: '8px'}}>
                <span style={{fontSize: '12px'}}>Hit enter after each email address that you want to bcc or deliver to</span>
              </div>
          }

          <div style={{display: 'flex', flexWrap: 'wrap', alignContent: 'start', padding: '6px', width: '100%'}}>
            { toRecipients.map(r => <Chip color={getEmailValidationColor(r)} variant="bordered"
                                          onClose={(c) => removeToRecipient(r)}><span onClick={() => showEmailStatus(r)}>To: {r}</span></Chip>) }
          </div>

          <div className='campaign-form-input'>
            <Input style={{marginTop: '8px', color: '#f0f0f0'}} label='To: Email Recipient'
                   type='text'
                   variant='bordered'
                   color='secondary'
                   value={toRecipient}
                   onKeyDown={(d) => {
                     if (d.keyCode === 13) {
                       d.keyCode = 0;
                       d.preventDefault();
                       let newToRecipients = [...toRecipients];
                       setRecipientsValidated(newToRecipients.concat(handleEmailInputString(d.target.value)), setToRecipients);
                       setToRecipient('');
                     }
                   }}
                   validationBehavior='aria'
                   placeholder='to_recipient@example.com'
                   onChange={(d) => setToRecipient(d.target.value)}
            />
            <div style={{display: 'flex'}}>
              <Tooltip isOpen={isToEmailTooltipOpen} content="Recipients specified will receive email as normal, visible recipients">
                <Badge onClick={() => {
                         if (isToEmailTooltipOpen) {
                           setIsToEmailTooltipOpen(false);
                         } else {
                           setIsToEmailTooltipOpen(true);
                         }
                       }}
                       variant='solid' color="primary" content="?" size="sm" />
              </Tooltip>
            </div>
          </div>

          <div style={{display: 'flex', flexWrap: 'wrap', alignContent: 'start', padding: '6px', width: '100%'}}>
            { ccRecipients.map(r => <Chip color={getEmailValidationColor(r)} variant="bordered"
                                          onClose={() => removeCcRecipient(r)}><span onClick={() => showEmailStatus(r)}>Cc: {r}</span></Chip>) }
          </div>

          <div className='campaign-form-input'>
            <Input style={{marginTop: '8px', color: '#f0f0f0'}} label='Cc: Email Recipient'
                   type='text'
                   variant='bordered'
                   color='secondary'
                   value={ccRecipient}
                   onKeyDown={(d) => {
                     if (d.keyCode === 13) {
                       d.keyCode = 0;
                       d.preventDefault();
                       let newCcRecipients = [...ccRecipients];
                       setRecipientsValidated(newCcRecipients.concat(handleEmailInputString(d.target.value)), setCcRecipients);
                       setCcRecipient('');
                     }
                   }}
                   validationBehavior='aria'
                   placeholder='cc_recipient@example.com'
                   onChange={(d) => setCcRecipient(d.target.value)}
            />
            <div style={{display: 'flex'}}>
              <Tooltip isOpen={isCcEmailTooltipOpen} content="Recipients specified will receive email as cc">
                <Badge onClick={() => {
                         if (isCcEmailTooltipOpen) {
                           setIsCcEmailTooltipOpen(false);
                         } else {
                           setIsCcEmailTooltipOpen(true);
                         }
                       }}
                       variant='solid' color="primary" content="?" size="sm" />
              </Tooltip>
            </div>
          </div>


          <div style={{display: 'flex', flexWrap: 'wrap', alignContent: 'start', padding: '6px', width: '100%'}}>
            { recipients.map(r => <Chip color={getEmailValidationColor(r)} variant="bordered"
                                        onClose={() => removeRecipient(r)}><span onClick={() => showEmailStatus(r)}>Bcc: {r}</span></Chip>) }
          </div>

          <div className='campaign-form-input'>
            <Input style={{marginTop: '8px', color: '#f0f0f0'}} label='Bcc: Email Recipient'
                   type='text'
                   variant='bordered'
                   color='secondary'
                   value={recipient}
                   onKeyDown={(d) => {
                     if (d.keyCode === 13) {
                       d.keyCode = 0;
                       d.preventDefault();
                       let newRecipients = [...recipients];
                       setRecipientsValidated(newRecipients.concat(handleEmailInputString(d.target.value)), setRecipients);
                       setRecipient('');
                     }
                   }}
                   validationBehavior='aria'
                   placeholder='bcc_recipient@example.com'
                   onChange={(d) => setRecipient(d.target.value)}
            />
            <div style={{display: 'flex'}}>
              <Tooltip isOpen={isBccEmailTooltipOpen} content="Recipients specified will receive email as BCC recipients">
                <Badge onClick={() => {
                         if (isBccEmailTooltipOpen) {
                           setIsBccEmailTooltipOpen(false);
                         } else {
                           setIsBccEmailTooltipOpen(true);
                         }
                       }}
                       variant='solid' color="primary" content="?" size="sm" />
              </Tooltip>
            </div>
          </div>
        </div>

        <div className='helper'>
          <div className='campaign-form-input'>
            <Textarea style={{color: '#202020'}} disableAnimation
                      classNames={{base: 'max-w-[800px]', input: 'resize-y min-h-[40px]'}}
                      isRequired variant='bordered'
                      label='Initial Email Body'
                      errorMessage={bodyError}
                      isInvalid={bodyError ? true : false}
                      validationBehavior='aria' size="lg"
                      value={mailerContent}
                      onChange={(d) => {
                        // 4000 becuase the URL passed to webmail also includes URI % encoding
                        if (d.target.value.length + headerByteCounter() > 4000) {
                          setBodyError('Warning: Some webmail clients such as Google will truncate messages that are longer than 4096 characters when launched from our campaign links and may cause errors preventing delivery of messages');
                        } else {
                          setBodyError();
                        }
                        setMailerContent(d.target.value)
                      }}
            />
            <div style={{display: 'flex'}}>
              <Tooltip isOpen={isBodyTooltipOpen} content="Initial body of email message.">
                <Badge onClick={() => {
                         if (isBodyTooltipOpen) {
                           setIsBodyTooltipOpen(false);
                         } else {
                           setIsBodyTooltipOpen(true);
                         }
                       }}
                       variant='solid' color="primary" content="?" size="sm" />
              </Tooltip>
            </div>
          </div>
          { user?.admin &&
              <div>
                <Textarea disableAnimation
                          classNames={{base: 'max-w-[800px]', input: 'resize-y min-h-[40px]'}}
                          variant='bordered'
                          label='Admin Prompt Inject'
                          value={promptInject}
                          validationBehavior='aria' size="lg"
                          onChange={(d) => setPromptInject(d.target.value)}
                />
             </div>
          }
        </div>
        <Accordion style={{fontColor: 'white'}}>
          <AccordionItem key='advanced' style={{color: 'white'}} title='Advanced'>
            <div className='slider-container'>
              <div style={{display: 'flex', position: 'relative', width: '100%', top: '8px', left: '82px'}}>
                { variability > 0.0 &&
                  <Tooltip isOpen={isVariabilityTooltipOpen} content="How variable should the message be">
                    <Badge onClick={() => {
                             if (isVariabilityTooltipOpen) {
                               setIsVariabilityTooltipOpen(false);
                             } else {
                               setIsVariabilityTooltipOpen(true);
                             }
                           }}
                           variant='solid' color="primary" content="?" size="sm" />
                  </Tooltip>
                }
              </div>
              <Slider color='warning'
                      className='max-w-md-[700px]'
                      defaultValue={variability}
                      maxValue={10}
                      minValue={0}
                      showTooltip={true}
                      step={0.1}
                      onChange={(value) => {setVariability(value); if (value === 0) { setTypos(false)}}}
                      label={`Variability${( variability === 0 || variability === 0.0 ) ? ' - No body changes' : ''}`}
              />
              <div style={{display: 'flex', position: 'relative', width: '100%', top: '8px', left: '47px'}}>
                <Tooltip isOpen={isToneTooltipOpen} content="How variable should the tone be">
                  <Badge onClick={() => {
                           if (isToneTooltipOpen) {
                             setIsToneTooltipOpen(false);
                           } else {
                             setIsToneTooltipOpen(true);
                           }
                         }}
                         variant='solid' color="primary" content="?" size="sm" />
                </Tooltip>
              </div>
              <Slider color='warning'
                      className='max-w-md-[700px]'
                      defaultValue={tone}
                      showTooltip={true}
                      maxValue={10}
                      minValue={0}
                      step={0.1}
                      onChange={(value) => setTone(value)}
                      label="Tone"
              />
            </div>
            <div style={{display: 'flex', flexDirection: 'column'}}>
              <div className='select-container'>
                <div style={{marginTop: '14px'}}>
                  <Checkbox isSelected={typos} onValueChange={toggleTypos}>
                    <span onClick={toggleTypos}>Periodically include commonly typoed words</span>
                  </Checkbox>
                </div>
                <div>
                  <Checkbox isDisabled={unpaidCampaign()}
                            isSelected={showFooter}  onValueChange={toggleFooter}>
                    <span onClick={toggleFooter}>Include ActivistMailer Footer</span>
                  </Checkbox>
                </div>
              </div>
            </div>
            <div style={{marginTop: '16px'}}>
              <Button onPress={() => checkCompliance()} color='success'>Check 501(c)(3) Compliance { fetchingCompliance && <Spinner size='sm' /> }</Button>
            </div>
          </AccordionItem>
        </Accordion>
        <div style={{marginTop: '1.3vh', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', paddingTop: '8px', minHeight: '80px'}}>
          <div style={{margin: '6px'}}>
            <Button onPress={() => cancelForm()}>Cancel</Button>
          </div>
          <div style={{margin: '6px'}}>
            <Button isDisabled={variability === 0} onPress={() => setShowPreview(true)} color='success'>Preview</Button>
          </div>
          <div style={{margin: '6px'}}>
            <Button color={verifyAllEmails() ? 'success' : 'warning'} type='submit'>{campaign?._id  ? 'Update' : 'Create'} Campaign</Button>
          </div>
        </div>
      </Form>
    </div>
  );
}

export default CampaignForm;
